import { Typography } from '@mui/material'
import { CvsAutoComplete } from '@components'
import { t } from '@lingui/core/macro'
import styled from '@emotion/styled'
import { useState, useEffect, useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { templateContext } from '@contexts/template-context'

const MarginTopTypography = styled(Typography)`
  margin-top: 12px;
`

export const ParentTemplateField = ({ form, setForm }) => {
  const { byId, fetchTemplates, fetchTemplate } = useContext(templateContext)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [selectedTemplateField, setSelectedTemplateField] = useState('')
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)

  // Fetch templates on component mount
  useEffect(() => {
    try {
      setIsLoadingTemplate(true)
      fetchTemplates()
      setSelectedTemplateId(form.parent?.template_id)
      setSelectedTemplateField(form.parent?.id)
    } catch (e) {
      console.log('Error fetching templates:', e)
      setSelectedTemplateId(null)
      setSelectedTemplateField(null)
    } finally {
      setIsLoadingTemplate(false)
    }
  }, [fetchTemplates])

  // // Fetch template fields when selectedTemplate changes
  const { data: templateFieldOptions, isFetching: isLoadingTemplateFields } = useQuery({
    queryKey: ['template', selectedTemplateId, 'fields'],
    queryFn: () => fetchTemplate(selectedTemplateId),
    select: template =>
      template?.template_fields
        .reduce((acc, templateField) => [...acc, { ...templateField }], [])
        .map(templateField => ({
          value: templateField.id,
          label: templateField.field.name,
          ...templateField,
        })) ?? [],
    enabled: !!selectedTemplateId,
  })

  const templateOptions = useMemo(() => {
    return Object.values(byId)
      .filter(template => template.id !== form.template_id)
      .map(template => ({
        value: template.id,
        label: template.name,
      }))
  }, [byId])

  const handleTemplateChange = val => {
    const value = typeof val === 'object' && !!val ? val.value : val
    setSelectedTemplateId(value)
    setSelectedTemplateField(null)
    setForm(prev => {
      const updatedForm = { ...prev, parent: null }
      return updatedForm
    })
  }

  const handleTemplateFieldChange = val => {
    const value = typeof val === 'object' && !!val ? val.value : val
    setSelectedTemplateField(value)

    const templateField = templateFieldOptions.find(option => option.value === value)

    setForm(prev => {
      const updatedForm = { ...prev, parent: templateField }
      return updatedForm
    })
  }

  return (
    <>
      <MarginTopTypography variant="subtitle1">{t`Parent Template Field`}</MarginTopTypography>

      <CvsAutoComplete
        disabled={templateOptions.length === 0}
        margin="dense"
        isMulti={false}
        label={t`Template`}
        options={templateOptions}
        selected={selectedTemplateId}
        onSelected={handleTemplateChange}
        style={{ marginTop: 0 }}
        isLoading={isLoadingTemplate}
        menuPlacement="auto"
        isClearable
        noWrap
      />

      <CvsAutoComplete
        disabled={!selectedTemplateId || isLoadingTemplateFields}
        margin="dense"
        isMulti={false}
        label={t`Template field`}
        options={templateFieldOptions}
        selected={selectedTemplateField}
        onSelected={handleTemplateFieldChange}
        style={{ marginTop: 0 }}
        isLoading={isLoadingTemplateFields}
        menuPlacement="auto"
        isClearable
        noWrap
      />
    </>
  )
}
